import React, { useState, useEffect, useRef } from 'react';
import { isManagedReadOnly, isManagedBotHidden, getManagedReadOnlyMessage } from '../utils/whatsappLifecycle';
import { MessageCircle, MessageSquare, X, ArrowDownRight, ArrowUpRight, Loader2, AlertCircle, Send, Smile, FileText, Paperclip, History, Check, CheckCheck, XCircle, Clock, Mail, Bell, ShoppingCart, Calendar, CheckCircle, Phone, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import AppendixWrapper from './common/AppendixWrapper';
import { useAppendix } from '../context/AppendixContext';
import WhatsAppTemplateButton from './WhatsAppTemplateButton';
import WhatsAppAttentionContext from './WhatsAppAttentionContext';
import WhatsAppAttachmentItem from './WhatsAppAttachmentItem';
import { useSalesData } from '../context/SalesDataContext';
import { useTramite } from '../context/TramiteContext';
import { useCapabilities } from '../hooks/useCapabilities';
import ChannelBadge from './ChannelBadge';
import ObjectTags from './common/ObjectTags';
import TagDisplayList from './common/TagDisplayList';

const TemplateIcons = {
    FileText,
    Smile,
    MessageSquare,
    Mail,
    Bell,
    ShoppingCart,
    Calendar,
    CheckCircle,
    Send,
    Phone
};

const getChannelLabel = (customName, channelName) => {
    const label = typeof customName === 'object' ? customName?.name : customName;
    return label || channelName;
};

const WhatsAppConversationAppendix = ({ isOpen, onClose, conversationId, onStolen, isHistorical = false, onOpenHistoricalConversation, onOpenCallTrace }) => {
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const { registerAppendix, setAppendixStatus } = useAppendix();
    const id = isHistorical ? 'whatsapp-history' : 'whatsapp-chat';

    useEffect(() => {
        registerAppendix(id, {
            title: isHistorical ? 'Historial WhatsApp' : 'Chat WhatsApp',
            icon: isHistorical ? History : MessageCircle
        });
    }, [registerAppendix, id, isHistorical]);

    useEffect(() => {
        setAppendixStatus(id, isOpen);
    }, [isOpen, setAppendixStatus, id]);
    const { authFetch, activeRole, agentInfo } = useAuth();
    const { currentTramite } = useTramite();
    const { extractedData, analyzedAttachments } = useSalesData();
    const [messages, setMessages] = useState([]);
    const [conversation, setConversation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [error, setError] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [templateAlert, setTemplateAlert] = useState(null);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const { hasCapability } = useCapabilities();
    const canTrace = hasCapability('calltrace');
    const COMMON_EMOJIS = ['😊', '👋', '👍', '🙏', '❤️', '✨', '⚡', '💡', '✅', '❌'];

    // Re-read computed capabilities after lifecycle/config events, including
    // pending returns which do not change the conversation row yet.
    useEffect(() => {
        if (!isOpen || !conversationId) return;
        let cancelled = false;
        const refresh = async () => {
            const res = await authFetch(`/api/whatsapp/conversation-detail/${conversationId}`);
            if (res.ok) {
                const detail = await res.json();
                if (!cancelled) {
                    setConversation(detail);
                    if (!isHistorical && isManagedBotHidden(detail)) closeRef.current?.();
                }
            }
        };
        const safeRefresh = () => refresh().catch(() => {});
        const onLifecycleUpdated = event => {
            if (String(event.detail?.conversation_id) !== String(conversationId)) return;
            setConversation(prev => ({ ...prev, ...event.detail }));
            safeRefresh();
        };
        window.addEventListener('wa-lifecycle-updated', onLifecycleUpdated);
        const channel = supabase.channel(`wa-lifecycle-${conversationId}`)
            .on('postgres_changes', { event: '*', schema: 'messaging', table: 'conversations', filter: `id=eq.${conversationId}` }, safeRefresh)
            .on('postgres_changes', { event: '*', schema: 'messaging', table: 'tenant_channel_decision_flows' }, safeRefresh)
            .on('postgres_changes', { event: '*', schema: 'espuma', table: 'whatsapp_bot_return_requests', filter: `conversation_id=eq.${conversationId}` }, safeRefresh)
            .subscribe();
        const interval = setInterval(safeRefresh, 60000);
        return () => {
            cancelled = true;
            window.removeEventListener('wa-lifecycle-updated', onLifecycleUpdated);
            clearInterval(interval);
            supabase.removeChannel(channel);
        };
    }, [isOpen, conversationId, authFetch, isHistorical]);

    useEffect(() => {
        if (!isOpen || !conversation?.decision_flow_managed || conversation.human_attention_pending || !conversation.service_window_until) return;
        const remaining = new Date(conversation.service_window_until).getTime() - Date.now();
        if (remaining <= 0) return;
        const timer = setTimeout(() => {
            setConversation(prev => ({ ...prev, can_operate: false }));
            if (!isHistorical) closeRef.current?.();
        }, remaining);
        return () => clearTimeout(timer);
    }, [isOpen, isHistorical, conversation?.decision_flow_managed, conversation?.service_window_until, conversation?.human_attention_pending]);

    // Load templates for the active role
    useEffect(() => {
        const fetchTemplates = async () => {
            if (!activeRole) {
                setAvailableTemplates([]);
                return;
            }
            try {
                const res = await authFetch(`/api/whatsapp/templates/my-role?role_id=${activeRole.id}`);
                if (res.ok) {
                    const data = await res.json();
                    setAvailableTemplates(data.templates || []);
                }
            } catch (err) {
                console.error('[WhatsApp Chat] Error fetching templates:', err);
                setAvailableTemplates([]);
            }
        };
        fetchTemplates();
    }, [activeRole, authFetch]);

    // Cleanup preview URL on unmount or change
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setSelectedFile(file);
        if (file.type.startsWith('image/')) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        } else {
            setPreviewUrl(null);
        }
    };

    const handlePaste = (e) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                // Ensure the blob has a name for the backend/Hub
                const file = new File([blob], `screenshot_${new Date().getTime()}.png`, { type: blob.type });
                setSelectedFile(file);
                const url = URL.createObjectURL(file);
                setPreviewUrl(url);
                break;
            }
        }
    };

    const removeFile = () => {
        setSelectedFile(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const handleAnalyze = (att) => {
        window.dispatchEvent(new CustomEvent('wa-analyze-file', { detail: att }));
    };

    const handleCompare = (att) => {
        window.dispatchEvent(new CustomEvent('wa-compare-file', { detail: att }));
    };

    const handleDownloadAttachment = async (att) => {
        try {
            const response = await authFetch(`/api/whatsapp/download/${att.id}`);
            if (!response.ok) throw new Error('Error al descargar');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = att.filename || att.original_filename || `archivo_${att.id}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error in download:', error);
            alert('No se pudo descargar el archivo.');
        }
    };



    useEffect(() => {
        if (!isOpen || !conversationId) return;

        const fetchData = async () => {
            // Only set loading to true if we don't have messages yet (initial load)
            // This prevents "flickering" or "resets" when a new message arrives
            if (messages.length === 0) {
                setLoading(true);
            }
            setError(null);
            try {
                // Check if we already have the conversation metadata in currentTramite
                let convData = null;
                const hasMetadata = currentTramite?.conversationMetadata && currentTramite.conversationMetadata.id === conversationId;
                
                if (hasMetadata) {
                    convData = currentTramite.conversationMetadata;
                    console.log('🔵 [INIT FROM TRAMITE] Conversation data from context:', {
                        conversation_id: convData?.id || convData?.conversation_id,
                        last_message_direction: convData?.last_message_direction,
                        last_message_type: convData?.last_message_type,
                        last_client_interaction: convData?.last_client_interaction
                    });
                    setConversation(convData);
                }

                // Fetch initial messages and detail
                const msgPromise = authFetch(`/api/whatsapp/messages/${conversationId}?limit=10`);
                const convPromise = authFetch(`/api/whatsapp/conversation-detail/${conversationId}`);

                const [msgRes, convRes] = await Promise.all([msgPromise, convPromise]);

                if (convRes && convRes.ok) {
                    const data = await convRes.json();
                    console.log('🔵 [INIT] Conversation data from backend:', {
                        conversation_id: data.conversation_id,
                        last_message_direction: data.last_message_direction,
                        last_message_type: data.last_message_type,
                        last_client_interaction: data.last_client_interaction
                    });
                    setConversation(data);
                    if (!isHistorical && isManagedBotHidden(data)) closeRef.current?.();
                }

                if (msgRes.ok) {
                    const msgData = await msgRes.json();
                    setMessages(msgData || []);
                } else if (messages.length === 0) {
                    throw new Error('Failed to fetch messages');
                }
                
                // Trigger a full messages fetch in background
                authFetch(`/api/whatsapp/messages/${conversationId}`).then(async (fullRes) => {
                    if (fullRes.ok) {
                        const fullData = await fullRes.json();
                        setMessages(fullData || []);
                    }
                }).catch(e => console.warn('Background full messages fetch failed', e));

            } catch (err) {
                console.error('Error fetching WA data:', err);
                if (messages.length === 0) {
                    setError('No se pudieron cargar los datos de la conversación');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // Subscripción a Realtime para mensajes
        const channel = supabase
            .channel(`wa-messages-${conversationId}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'messaging', table: 'messages', filter: `conversation_id=eq.${conversationId}` }, async (payload) => {
                const newMsg = payload.new;
                
                // Si el mensaje tiene attachments (imagen, video, audio, documento), fetch completo
                const hasAttachments = newMsg.type && ['image', 'video', 'audio', 'document', 'sticker'].includes(newMsg.type);
                if (hasAttachments) {
                    try {
                        // Fetch de todos los mensajes para obtener el mensaje con attachments
                        const fullRes = await authFetch(`/api/whatsapp/messages/${conversationId}`);
                        if (fullRes.ok) {
                            const fullData = await fullRes.json();
                            setMessages(fullData || []);
                            return; // Ya actualizamos todos los mensajes
                        }
                    } catch (err) {
                        console.warn('Error fetching message with attachments:', err);
                        // Continuar con el mensaje sin attachments
                    }
                }
                
                setMessages(prev => {
                    if (prev.some(m => m.id === newMsg.id)) return prev;
                    return [...prev, newMsg].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                });

                // Actualizar los campos de último mensaje en conversation
                setConversation(prev => {
                    if (!prev) return prev;
                    const updated = {
                        ...prev,
                        last_message_direction: newMsg.direction,
                        last_message_type: newMsg.type,
                        // Si es inbound, actualizar también last_client_interaction
                        last_client_interaction: (newMsg.direction === 'INBOUND' || newMsg.direction === 'inbound' || newMsg.direction === 'in')
                            ? newMsg.created_at
                            : prev.last_client_interaction
                    };
                    console.log('🟢 [NEW MSG] Actualizado conversation state:', {
                        msg_direction: newMsg.direction,
                        msg_type: newMsg.type,
                        new_last_message_direction: updated.last_message_direction,
                        new_last_message_type: updated.last_message_type,
                        new_last_client_interaction: updated.last_client_interaction
                    });
                    return updated;
                });

                // Catcher: ingestar mensajes entrantes para extracción de campos
                try {
                    const isInbound = newMsg.direction === 'in' || newMsg.direction === 'inbound';
                    const text = newMsg.text_body || newMsg.transcript_text || newMsg.body || newMsg.text || newMsg.content;
                    if (isInbound && text && hasCapability('catcher')) {
                        authFetch('/api/catcher/ingest', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                conversation_id: conversationId,
                                message_id: newMsg.id,
                                text,
                                direction: 'in'
                            })
                        }).catch(err => console.warn('[Catcher ingest] error:', err));
                    }
                } catch (err) {
                    console.warn('[Catcher ingest] exception:', err);
                }
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'messaging', table: 'messages', filter: `conversation_id=eq.${conversationId}` }, (payload) => {
                const updatedMsg = payload.new;
                setMessages(prev => prev.map(m => m.id === updatedMsg.id ? { ...m, ...updatedMsg } : m));
            })
            // Suscribirse también a cambios en la conversación para actualizar el status
            .on('postgres_changes', { event: 'UPDATE', schema: 'messaging', table: 'conversations' }, (payload) => {
                const idObj = payload.new?.id || payload.old?.id;
                if (idObj === conversationId) {
                    console.log(`🟡 [CONV UPDATE] UPDATE en conversation ${conversationId}:`, payload.new);
                    // Preservar campos calculados que no vienen en el update de Supabase
                    setConversation(prev => {
                        if (!prev) return payload.new;
                        const updated = {
                            ...prev,
                            ...payload.new,
                            // Preservar estos campos calculados del fetch inicial
                            last_message_direction: prev.last_message_direction,
                            last_message_type: prev.last_message_type,
                            last_client_interaction: prev.last_client_interaction
                        };
                        console.log('🟡 [CONV UPDATE] Campos preservados:', {
                            last_message_direction: updated.last_message_direction,
                            last_message_type: updated.last_message_type,
                            last_client_interaction: updated.last_client_interaction
                        });
                        return updated;
                    });
                }
            })
            .subscribe();

        return () => { supabase.removeChannel(channel); };
        // currentTramite.conversationMetadata and messages.length omitted intentionally
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, conversationId, authFetch, currentTramite?.id, isHistorical]);

    // Listener de Supabase para cambios en etiquetas (object_tags)
    useEffect(() => {
        if (!conversationId || !isOpen) return;

        const tagsChannel = supabase
            .channel(`wa-tags-${conversationId}`)
            .on('postgres_changes', { 
                event: '*', 
                schema: 'espuma', 
                table: 'object_tags',
                filter: `object_id=eq.${conversationId}` 
            }, async (payload) => {
                console.log(`[WA Appendix] 🏷️ Tag change detected for conversation ${conversationId}:`, payload);
                // Re-fetch conversation detail to get updated tags
                try {
                    const res = await authFetch(`/api/whatsapp/conversation-detail/${conversationId}`);
                    if (res.ok) {
                        const data = await res.json();
                        setConversation(prev => ({ ...prev, tags: data.tags }));
                    }
                } catch (err) {
                    console.error('[WA Appendix] Error refreshing tags:', err);
                }
            })
            .subscribe();

        return () => { supabase.removeChannel(tagsChannel); };
    }, [conversationId, isOpen, authFetch]);

    // Listener de evento custom para cambios de etiquetas locales
    useEffect(() => {
        if (!conversationId) return;

        const handleTagsChanged = async (event) => {
            const { objectType, objectId } = event.detail;
            if (objectType === 'whatsapp_conversation' && objectId === conversationId) {
                console.log(`[WA Appendix] 🏷️ Custom event: tags changed for ${conversationId}`);
                // Re-fetch conversation detail to get updated tags
                try {
                    const res = await authFetch(`/api/whatsapp/conversation-detail/${conversationId}`);
                    if (res.ok) {
                        const data = await res.json();
                        setConversation(prev => ({ ...prev, tags: data.tags }));
                    }
                } catch (err) {
                    console.error('[WA Appendix] Error refreshing tags:', err);
                }
            }
        };

        window.addEventListener('objectTagsChanged', handleTagsChanged);
        return () => window.removeEventListener('objectTagsChanged', handleTagsChanged);
    }, [conversationId, authFetch]);

    // Auto-scroll to bottom
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    useEffect(() => {
        adjustTextareaHeight();
    }, [newMessage]);


    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) + ' ' + d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
    };

    // Helper to render delivery status icon
    const renderDeliveryStatus = (status) => {
        if (!status) return null;
        
        const statusLower = status.toLowerCase();
        
        if (statusLower === 'read') {
            return (
                <div className="flex items-center gap-0.5" title="Leído">
                    <CheckCheck size={11} className="text-blue-400" />
                </div>
            );
        }
        
        if (statusLower === 'delivered') {
            return (
                <div className="flex items-center gap-0.5" title="Entregado">
                    <CheckCheck size={11} className="text-slate-400" />
                </div>
            );
        }
        
        if (statusLower === 'sent') {
            return (
                <div className="flex items-center gap-0.5" title="Enviado">
                    <Check size={11} className="text-slate-400" />
                </div>
            );
        }
        
        if (statusLower === 'failed') {
            return (
                <div className="flex items-center gap-0.5" title="Error al enviar">
                    <XCircle size={11} className="text-red-400" />
                </div>
            );
        }
        
        return null;
    };

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (isManagedReadOnly(conversation) || isHistorical) return;
        if ((!newMessage.trim() && !selectedFile) || !conversationId || sending) return;

        setSending(true);
        setError(null);
        try {
            let res;
            if (selectedFile) {
                const formData = new FormData();
                // Explicitly pass the filename in the 3rd argument of append
                formData.append('file', selectedFile, selectedFile.name || 'file');
                formData.append('caption', newMessage.trim());

                res = await authFetch(`/api/whatsapp/inbox/conversations/${conversationId}/send-attachment`, {
                    method: 'POST',
                    body: formData
                });
            } else {
                res = await authFetch('/api/whatsapp/send-text', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        conversation_id: conversationId,
                        text: newMessage.trim()
                    })
                });
            }

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Error al enviar el mensaje');
            }

            setNewMessage('');
            removeFile();
            setShowEmojiPicker(false);
        } catch (err) {
            console.error('Error sending WA message:', err);
            setError(err.message || 'No se pudo enviar el mensaje');
        } finally {
            setSending(false);
        }
    };

    const addEmoji = (emoji) => {
        setNewMessage(prev => prev + emoji);
    };

    const handleTemplateError = (err, templateName = 'esta plantilla') => {
        if (err.status === 409) {
            setTemplateAlert({
                type: 'warning',
                message: err.message || `Ya hay un proceso abierto para este contacto al enviar ${templateName}.`
            });
            // Auto hide after 10 seconds
            setTimeout(() => setTemplateAlert(null), 10000);
        } else {
            setError(err.message || 'Error al enviar plantilla');
        }
    };

    const isClosed = conversation?.status !== 'open';

    // Calculate conversation state (awaiting client vs expired)
    const conversationState = React.useMemo(() => {
        console.log('⚙️ [STATE CALC] Recalculando estado:', {
            last_message_direction: conversation?.last_message_direction,
            last_message_type: conversation?.last_message_type,
            last_client_interaction: conversation?.last_client_interaction
        });
        
        // Primero verificar si el último mensaje es un template outbound
        const isOutboundTemplate = 
            (conversation?.last_message_direction === 'OUTBOUND' || 
             conversation?.last_message_direction === 'outbound' || 
             conversation?.last_message_direction === 'out') && 
            conversation?.last_message_type === 'system';
        
        console.log('⚙️ [STATE CALC] isOutboundTemplate:', isOutboundTemplate);
        
        if (isOutboundTemplate) {
            console.log('⚙️ [STATE CALC] ✅ ESPERANDO CLIENTE');
            return { isAwaitingClientReply: true, isExpired: false };
        }
        
        // Si no es template outbound, aplicar lógica de caducidad
        if (!conversation?.last_client_interaction) {
            console.log('⚙️ [STATE CALC] ❌ CADUCADA (sin interacción)');
            return { isAwaitingClientReply: false, isExpired: true };
        }
        
        const now = Date.now();
        const lastInteractionTime = new Date(conversation.last_client_interaction).getTime();
        const hoursSinceLastClient = (now - lastInteractionTime) / (1000 * 60 * 60);
        
        const expired = hoursSinceLastClient > 24;
        console.log('⚙️ [STATE CALC]', expired ? '❌ CADUCADA (>24h)' : '✅ NORMAL', `(${hoursSinceLastClient.toFixed(1)}h)`);
        
        return { 
            isAwaitingClientReply: false, 
            isExpired: expired
        };
    }, [conversation?.last_client_interaction, conversation?.last_message_direction, conversation?.last_message_type]);

    const { isAwaitingClientReply, isExpired } = conversationState;
    const managedReadOnly = isManagedReadOnly(conversation);

    const isLockedByOtherAgent = !isHistorical && conversation?.assigned_agent_id &&
        String(conversation.assigned_agent_id).toLowerCase() !== String(agentInfo?.email).toLowerCase();

    const handleTakeControl = async () => {
        if (isManagedReadOnly(conversation)) return;
        if (!conversationId || !agentInfo) return;
        try {
            const response = await authFetch('/api/whatsapp/open-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    agent_email: agentInfo.email,
                    agent_id: agentInfo.id,
                    phone: conversation?.phone_number,
                    contact_id: conversation?.contact_id,
                    force: true
                })
            });
            if (!response.ok) throw new Error('Error al tomar control');
            setConversation(prev => prev ? { ...prev, assigned_agent_id: agentInfo.email } : prev);
        } catch (err) {
            console.error('[WA Appendix] Error al tomar control:', err);
        }
    };

    return (
        <AppendixWrapper 
            id={id} 
            title={isHistorical ? "Historial WhatsApp" : "Chat WhatsApp"} 
            icon={isHistorical ? History : MessageCircle} 
            className={`w-[420px] h-[60vh] ${isHistorical ? 'ring-1 ring-blue-500/30' : ''}`}
            onClose={onClose}
            visible={isOpen}
            defaultPosition={isHistorical 
                ? { x: window.innerWidth - 900, y: window.innerHeight - 600 }
                : { x: window.innerWidth - 450, y: window.innerHeight - 600 }
            }
        >
            {/* Content Wrapper */}
            <div className={`flex flex-col h-full ${isHistorical ? 'bg-slate-900' : 'bg-slate-950'}`}>
                <WhatsAppAttentionContext key={conversationId} conversationId={conversationId} lastAgent={conversation?.last_assigned_agent_id} />
                {/* Header Info (Sub-header) */}
                <div className={`px-5 py-2 ${isHistorical ? 'bg-blue-950/20 border-blue-900/20' : 'bg-emerald-950/20 border-emerald-900/20'} border-b flex flex-col gap-2`}>
                    {/* First row: Status, Channel, Trace button */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap flex-1">
                            {!isClosed && (
                                <>
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isHistorical ? 'bg-blue-500' : isLockedByOtherAgent ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} />
                                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest whitespace-nowrap">
                                        {isHistorical ? 'Modo Histórico (Solo Lectura)' : isLockedByOtherAgent ? 'Solo Lectura (Bloqueada)' : 'Sesión Activa'}
                                    </span>
                                </>
                            )}
                            {conversation?.channel_color && (
                                <ChannelBadge
                                    color={conversation.channel_color}
                                    channel_name={conversation.channel_name}
                                    custom_name={conversation.channel_custom_name}
                                    size="sm"
                                    showPhoneActions={false}
                                />
                            )}
                            {getChannelLabel(conversation?.channel_custom_name, conversation?.channel_name) && (
                                <span className="text-[9px] text-slate-500 truncate max-w-[80px]">
                                    {getChannelLabel(conversation.channel_custom_name, conversation.channel_name)}
                                </span>
                            )}
                        </div>
                        {conversationId && canTrace && !isHistorical && !isLockedByOtherAgent && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => onOpenCallTrace(conversation?.phone_number ? [conversation.phone_number] : [])}
                                    className="flex items-center gap-1.5 px-3 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 border border-blue-500/20 rounded-md transition-all group pointer-events-auto"
                                    title="Ver historial de interacciones"
                                >
                                    <History size={10} />
                                    Trace
                                </button>
                            </div>
                        )}
                    </div>
                    
                    {/* Second row: Tags Section */}
                    {conversationId && (
                        <div className="flex items-center w-full">
                            {isHistorical || isLockedByOtherAgent ? (
                                conversation?.tags && conversation.tags.length > 0 && (
                                    <div className="flex-1">
                                        <TagDisplayList tags={conversation.tags} maxDisplay={3} />
                                    </div>
                                )
                            ) : (
                                <ObjectTags 
                                    objectType="whatsapp_conversation"
                                    objectId={conversationId}
                                    onTagsChanged={(newTags) => {
                                        setConversation(prev => ({ ...prev, tags: newTags }));
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>

            {/* Content */}
            <div className={`flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar ${isHistorical ? 'bg-slate-900/40' : 'bg-slate-950/20'}`}>
                {loading && (
                    <div className="flex flex-col items-center justify-center py-20 opacity-70">
                        <Loader2 size={24} className="text-emerald-500 animate-spin mb-3" />
                        <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">Cargando conversación...</span>
                    </div>
                )}

                {error && (
                    <div className="flex flex-col items-center justify-center p-6 bg-red-500/5 border border-red-500/10 rounded-2xl mx-2 mt-4">
                        <AlertCircle size={24} className="text-red-500 mb-3" />
                        <span className="text-xs text-red-200 text-center leading-relaxed font-medium">{error}</span>
                    </div>
                )}

                {!loading && !error && messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 opacity-30 px-10 text-center">
                        <MessageCircle size={40} className="text-slate-600 mb-4" />
                        <span className="text-xs text-slate-400 font-medium">No hay mensajes previos en esta conversación</span>
                    </div>
                )}

                {!loading && !error && messages.map((msg) => {
                    const isOutbound = msg.direction === 'outbound';
                    return (
                        <div key={msg.id} className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'} max-w-[90%] ${isOutbound ? 'ml-auto' : ''}`}>
                            <div className={`rounded-2xl px-4 py-3 shadow-sm ${
                                isOutbound 
                                ? 'bg-emerald-600/10 border border-emerald-500/20 text-emerald-50 rounded-tr-none' 
                                : 'bg-slate-900 border border-slate-800 text-slate-100 rounded-tl-none'
                            }`}>
                                {/* Template badge */}
                                {msg.template_name && (
                                    <div className="text-[9px] font-black uppercase tracking-widest opacity-50 mb-1.5 border-b border-emerald-500/10 pb-1 flex items-center gap-1.5">
                                        <FileText size={10} className="text-emerald-500" />
                                        <span className="text-emerald-400">Plantilla: {msg.template_name}</span>
                                    </div>
                                )}
                                {/* Generic multimedia badge for non-text without template info */}
                                {!msg.template_name && msg.type !== 'text' && (!msg.attachments || msg.attachments.length === 0) && (
                                    <div className="text-[9px] font-black uppercase tracking-widest opacity-40 mb-1.5 border-b border-white/5 pb-1 flex items-center gap-1">
                                        Multimedia
                                    </div>
                                )}
                                {(!msg.attachments || msg.attachments.length === 0) && (
                                    <p className="text-xs whitespace-pre-wrap leading-relaxed">{msg.text_body || '[Contenido Multimedia]'}</p>
                                )}
                                {msg.attachments && msg.attachments.length > 0 && (
                                    <div className="flex flex-col gap-1.5">
                                        {msg.text_body && <p className="text-xs whitespace-pre-wrap leading-relaxed mb-2">{msg.text_body}</p>}
                                        {msg.attachments.map(att => (
                                            <WhatsAppAttachmentItem 
                                                key={att.id} 
                                                att={att} 
                                                extractedData={extractedData}
                                                analyzedFiles={analyzedAttachments}
                                                onAnalyze={handleAnalyze}
                                                onCompare={handleCompare}
                                                onDownload={handleDownloadAttachment}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 px-1 opacity-60">
                                {isOutbound ? <ArrowUpRight size={10} className="text-emerald-400" /> : <ArrowDownRight size={10} className="text-slate-400" />}
                                {!isOutbound && conversation?.contact_name && (
                                    <span className="text-[9px] font-semibold text-slate-300">
                                        {conversation.contact_name}
                                    </span>
                                )}
                                <span className="text-[9px] font-mono text-slate-200">{formatDate(msg.created_at)}</span>
                                {isOutbound && msg.delivery_status && (
                                    <>
                                        <span className="text-[9px] text-slate-600">•</span>
                                        {renderDeliveryStatus(msg.delivery_status)}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Locked by other agent banner (read-only mode) */}
            {!isHistorical && !managedReadOnly && isLockedByOtherAgent && (
                <div className="p-3 border-t border-amber-500/20 bg-amber-500/10 flex items-center gap-3">
                    <Lock size={16} className="text-amber-400 shrink-0" />
                    <p className="flex-1 text-[11px] text-amber-200 font-medium leading-relaxed">
                        Esta conversación está siendo atendida por <span className="font-bold">{conversation.assigned_agent_id}</span>. Modo solo lectura.
                    </p>
                    <button
                        onClick={handleTakeControl}
                        className="px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors shrink-0"
                    >
                        Tomar Control
                    </button>
                </div>
            )}

            {/* Input Section */}
            {managedReadOnly && (
                <div className="p-3 text-xs text-amber-300 border-t border-slate-800">
                    {getManagedReadOnlyMessage(conversation)}
                </div>
            )}
            {!isHistorical && !managedReadOnly && !isLockedByOtherAgent && (
                <div className={`p-4 border-t border-slate-900 transition-colors duration-300 ${isClosed ? 'bg-slate-950' : 'bg-slate-950/80'} relative z-10`}>
                {/* Attachment Preview Area */}
                {selectedFile && (
                    <div className="mb-3 animate-in fade-in slide-in-from-bottom-2">
                        <div className="relative group bg-slate-900/50 border border-emerald-500/20 rounded-xl p-2 flex items-center gap-3">
                            {previewUrl ? (
                                <img src={previewUrl} alt="Preview" className="w-12 h-12 rounded-lg object-cover border border-slate-800" />
                            ) : (
                                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                                    <FileText size={20} className="text-emerald-500" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold text-slate-200 truncate">{selectedFile.name || 'Imagen del portapapeles'}</p>
                                <p className="text-[9px] text-slate-500 font-mono">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <button 
                                onClick={removeFile}
                                type="button"
                                className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-500 transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Expired Conversation Banner */}
                {isExpired && !isHistorical && (
                    <div className="absolute bottom-full left-0 right-0 p-3 bg-red-500/10 border-t border-red-500/20 backdrop-blur-md flex items-start gap-3 z-50">
                        <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-[11px] text-red-200 font-medium leading-relaxed">
                                Esta conversación ha caducado. Han pasado más de 24 horas desde la última actividad del cliente.
                            </p>
                        </div>
                    </div>
                )}

                {/* Awaiting Client Reply Banner */}
                {!isExpired && isAwaitingClientReply && !isHistorical && (
                    <div className="absolute bottom-full left-0 right-0 p-3 bg-sky-500/10 border-t border-sky-500/20 backdrop-blur-md flex items-start gap-3 z-50">
                        <Clock size={16} className="text-sky-400 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-[11px] text-sky-200 font-medium leading-relaxed">
                                Esperando respuesta del cliente. Se envió una plantilla y el cliente aún no ha respondido.
                            </p>
                        </div>
                    </div>
                )}

                {/* 409 Specific Alert Banner */}
                {templateAlert && (
                    <div className="absolute bottom-full left-0 right-0 p-3 bg-amber-500/10 border-t border-amber-500/20 backdrop-blur-md flex items-start gap-3 animate-in slide-in-from-bottom-2 duration-300 z-50">
                        <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-[11px] text-amber-200 font-medium leading-relaxed">
                                {templateAlert.message}
                            </p>
                        </div>
                        <button onClick={() => setTemplateAlert(null)} className="p-1 hover:bg-amber-500/20 rounded text-amber-500">
                            <X size={14} />
                        </button>
                    </div>
                )}

                
                {showEmojiPicker && (
                    <div className="flex gap-2 mb-3 p-2 bg-slate-900 border border-slate-800 rounded-xl overflow-x-auto scrollbar-none animate-in fade-in slide-in-from-bottom-2">
                        {COMMON_EMOJIS.map(emoji => (
                            <button
                                key={emoji}
                                onClick={() => addEmoji(emoji)}
                                className="text-xl hover:scale-125 transition-transform"
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
                <form onSubmit={handleSendMessage} className="flex gap-3 items-end">
                    {availableTemplates.length > 0 && (
                        <div className="flex flex-col gap-1 max-h-28 overflow-y-auto pr-1">
                            {availableTemplates.map(template => {
                                const Icon = TemplateIcons[template.icon_name] || FileText;
                                return (
                                    <WhatsAppTemplateButton
                                        key={template.id}
                                        templateId={template.id}
                                        title={template.name}
                                        templateName={template.name}
                                        conversationId={conversationId}
                                        icon={Icon}
                                        className={'p-2 bg-slate-800 hover:bg-slate-700 rounded-md text-slate-300'}
                                        onError={(err) => handleTemplateError(err, template.name)}
                                    />
                                );
                            })}
                        </div>
                    )}
                    <div className="relative flex-1">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isExpired || isAwaitingClientReply}
                            className={`absolute left-3 bottom-2.5 p-1.5 rounded-lg transition-all z-20 ${(isExpired || isAwaitingClientReply) ? 'opacity-50 cursor-not-allowed' : selectedFile ? 'text-emerald-400 bg-emerald-500/10' : 'text-slate-500 hover:text-emerald-400 hover:bg-slate-800'}`}
                            title="Adjuntar archivo o imagen"
                        >
                            <Paperclip size={18} />
                        </button>
                        <textarea
                            ref={textareaRef}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder={
                                isAwaitingClientReply ? "Esperando respuesta del cliente" : 
                                isExpired ? "Conversación caducada" : 
                                "Escribe un mensaje..."
                            }
                            onPaste={handlePaste}
                            disabled={isExpired || isAwaitingClientReply}
                            className={`w-full bg-slate-900 border border-slate-800 rounded-2xl pl-11 pr-10 py-3 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500/30 transition-all resize-none max-h-32 custom-scrollbar overflow-y-auto ${(isExpired || isAwaitingClientReply) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            style={{ minHeight: '44px' }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            disabled={isExpired || isAwaitingClientReply}
                            className={`absolute right-3 bottom-3 transition-colors ${(isExpired || isAwaitingClientReply) ? 'opacity-50 cursor-not-allowed text-slate-700' : showEmojiPicker ? 'text-emerald-400' : 'text-slate-600 hover:text-emerald-400'}`}
                        >
                            <Smile size={20} />
                        </button>
                    </div>
                    <button
                        type="submit"
                        disabled={(!newMessage.trim() && !selectedFile) || sending || isExpired || isAwaitingClientReply}
                        className="p-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-20 disabled:grayscale text-white rounded-2xl transition-all shadow-xl shadow-emerald-950/20 shrink-0"
                    >
                        {sending ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : (
                            <Send size={20} />
                        )}
                    </button>
                </form>
            </div>
            )}
            </div>
        </AppendixWrapper>
    );
};

export default WhatsAppConversationAppendix;

